import React, { useState, useEffect } from "react";
import Pie from "@amzn/meridian/pie";
import Legend, { LegendProvider } from "@amzn/meridian/legend";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Heading from "@amzn/meridian/heading";
import "src/components/pages/validationWorkflow/groundTruthReview/modelGroundTruthReview.scss";
import Loader from "@amzn/meridian/loader";
import { getRatio } from "src/components/pages/audit/helpers";

export interface DonutChartProps {
  key: string;
  value: number;
  color: string;
  label: string;
}

interface DonutChartComponentProps {
  totalCount: number;
  reviewedCount: number;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  labels?: {
    reviewed: string;
    unreviewed: string;
    centerLabel: string;
    errorMessage?: string;
  };
}

const defaultLabels = {
  reviewed: "Completed",
  unreviewed: "Pending",
  centerLabel: "pending",
};

const DonutChart = ({
  totalCount,
  reviewedCount,
  isLoading = false,
  title = "Total ground truth",
  subtitle = "Cases yet to be reviewed",
  labels = defaultLabels,
}: DonutChartComponentProps) => {
  const widthRatio = 0.07;
  const [pieWidth, setPieWidth] = useState(window.innerWidth * widthRatio);

  useEffect(() => {
    const handleResize = () => {
      setPieWidth(window.innerWidth * widthRatio);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const unReviewedCount = totalCount - reviewedCount;

  const data: DonutChartProps[] = [
    {
      value: unReviewedCount,
      key: "unreviewed",
      color: "#D9D9D9",
      label: labels.unreviewed,
    },
    {
      value: reviewedCount,
      key: "reviewed",
      color: "#36c2b4",
      label: labels.reviewed,
    },
  ];

  if (isLoading) return <Loader />;

  return totalCount === 0 ? (
    <Text>{labels.errorMessage}</Text>
  ) : (
    <Row spacingInset={"400"} className="donut-chart">
      <LegendProvider data={data}>
        <Row spacing="400" alignmentHorizontal="center" alignmentVertical="top">
          <Pie width={pieWidth} data={data} donutRatio={0.7}>
            <Text type="h100" tag="div">
              {getRatio(data)}
            </Text>
            <Text type="b100" tag="div">
              {labels.centerLabel}
            </Text>
          </Pie>
          <Column>
            <Heading id="animals-one" level={6}>
              {`${title}: ${totalCount}`}
            </Heading>
            <Text>{subtitle}</Text>
            <Legend direction="vertical" values={(item) => item?.value?.toLocaleString() || "0"} />
          </Column>
        </Row>
      </LegendProvider>
    </Row>
  );
};

export default DonutChart;
